<script setup lang="ts">
definePageMeta({ prerender: true })

const { data: about } = await useAsyncData('about', () => queryCollection('about').first())

useSeoMeta({
  description: () => about.value?.seo.description,
  ogDescription: () => about.value?.seo.ogDescription ?? about.value?.seo.description,
  ogTitle: () => about.value?.seo.ogTitle ?? about.value?.seo.title,
  title: () => about.value?.seo.title,
  twitterDescription: () => about.value?.seo.ogDescription ?? about.value?.seo.description,
  twitterTitle: () => about.value?.seo.ogTitle ?? about.value?.seo.title,
})

defineOgImageComponent('Default', {
  description: () => about.value?.seo.ogDescription ?? about.value?.seo.description ?? '',
  eyebrow: 'signalfire.games / about',
  title: 'About',
})

const siteConfig = useSiteConfig()
useSchemaOrg([
  defineWebPage({
    '@type': [ 'WebPage', 'AboutPage' ],
    ...(about.value?.lastUpdated ? { dateModified: about.value.lastUpdated } : {}),
  }),
  defineBreadcrumb({
    itemListElement: [
      { item: siteConfig.url, name: 'Home' },
      { item: `${siteConfig.url}/about/`, name: 'About' },
    ],
  }),
])
</script>

<template>
  <div v-if="about">
    <!-- ===== Hero ===== -->
    <SfgSheet>
      <div class="mx-auto max-w-3xl">
        <SfgEyebrow variant="inverted">
          {{ about.hero.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="1" class="text-brand-chalk mt-5">
          {{ about.hero.heading }}
        </SfgHeading>
        <p class="font-body text-body mt-6 text-brand-chalk">
          {{ about.hero.lead }}
        </p>
      </div>
    </SfgSheet>

    <!-- ===== Story ===== -->
    <SfgPaper>
      <div class="mx-auto max-w-3xl">
        <SfgEyebrow variant="outline">
          {{ about.story.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="mt-5">
          {{ about.story.heading }}
        </SfgHeading>
        <div class="flex flex-col gap-5 mt-8 font-body text-body">
          <p v-for="(p, i) in about.story.paragraphs" :key="i">
            {{ p }}
          </p>
        </div>
      </div>
    </SfgPaper>

    <!-- ===== Principles ===== -->
    <SfgSheet :padded="false">
      <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <div class="max-w-3xl">
          <SfgEyebrow variant="inverted">
            {{ about.principles.eyebrow }}
          </SfgEyebrow>
          <SfgHeading :level="2" class="text-brand-chalk mt-5">
            {{ about.principles.heading }}
          </SfgHeading>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          <div
            v-for="item in about.principles.items"
            :key="item.title"
            class="flex flex-col"
          >
            <h3 class="font-display uppercase text-[14px] tracking-[0.14em] text-brand-yellow">
              {{ item.title }}
            </h3>
            <p class="font-body text-body mt-3 text-brand-chalk">
              {{ item.body }}
            </p>
          </div>
        </div>
      </div>
    </SfgSheet>

    <!-- ===== Who runs it ===== -->
    <SfgPaper>
      <div class="mx-auto max-w-3xl">
        <SfgEyebrow variant="outline">
          {{ about.who.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="mt-5">
          {{ about.who.heading }}
        </SfgHeading>
        <p class="font-body text-body mt-6">
          {{ about.who.body }}
        </p>
        <a
          :href="about.who.linkTo"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 mt-6 font-display uppercase text-[12px] tracking-[0.14em] text-brand-red hover:text-brand-ink transition-colors"
        >
          {{ about.who.linkLabel }} →
        </a>
      </div>
    </SfgPaper>

    <!-- ===== Closing CTA ===== -->
    <SfgSheet>
      <div class="mx-auto max-w-2xl text-center">
        <SfgEyebrow variant="inverted">
          {{ about.closing.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="text-brand-chalk mt-5">
          {{ about.closing.heading }}
        </SfgHeading>
        <p class="font-body text-body mt-6 text-brand-chalk">
          {{ about.closing.body }}
        </p>
        <div class="mt-8 flex justify-center">
          <SfgButton
            variant="primary"
            size="md"
            :to="about.closing.cta.to"
          >
            {{ about.closing.cta.label }}
          </SfgButton>
        </div>
      </div>
    </SfgSheet>
  </div>
</template>
